import React, { useState } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES } from "../../utils/constants";
import { getScenarioNumByPath, getTimer } from "../../utils/helpers";
import { updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import {
  modalConfirm,
  modalQuestionResolution,
  modalHint,
  modalHintShow
} from "../../utils/logger";
import { IconWriting } from "@tabler/icons-react";
import CheckButton from "../../components/CheckButton";
import estasseguro from "../../assets/bg/estasseguro-avatar-violet.png";
import respuestacorrecta from "../../assets/bg/correcto-avatar.png";
import respuestaincorrecta from "../../assets/bg/incorrecto-avatar.png";
import { useVideoWithSound } from "../../hooks/useVideoWithSound";
import btnPistaViolet from "../../assets/btnPistaViolet.png";
import imageHint from "../../assets/hints/imageHintViolet.png";
import imageHintUsed from "../../assets/btnPistaViolet.png";
import unIntento from "../../assets/un-intento-violet.png"
import dosIntentos from "../../assets/dos-intentos-violet.png"


function Scenario3EvaluacionGeriatricos({ intl }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const group = useSelector((state) => state.group);

  const activeScenario = getScenarioNumByPath(location.pathname);

  const { videoRef, canPlayWithSound } = useVideoWithSound({
    autoPlay: true,
    volume: 0.8
  });

  const QUIZ_ID = QUIZZES.EVALUACION_GERIATRICOS;
  const SCENARIO_ID = SCENARIOS.SCENARIO_3;

  const [inputValue, setInputValue] = useState("");
  const [isCompleted, setIsCompleted] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  const [hintPenalty, setHintPenalty] = useState(0);
  const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // 2 intentos con envío único (suma final)
  const [attempt, setAttempt] = useState(1);            // 1..2
  const [pendingScore, setPendingScore] = useState(0);  // acumulado a enviar al final

  const NUMBERATTEMPTS = 2;
  const CORRECT_ANSWER = 65;
  const CORRECT_ANSWER_POINTS = 10;
  const WRONG_ANSWER_POINTS = -5;
  const CORRECT_ANSWER_POINTS_2 = 3;
  const WRONG_ANSWER_POINTS_2 = 0;

  async function submitAnswer(finalScore, correctFinal) {
    const totalTime = getTimer();
    dispatch(updateTime(totalTime, 1));

    try {
      const res = await postAnswer({
        scenario: SCENARIO_ID,
        quiz: QUIZ_ID,
        group: group?._id,
        score: finalScore,                // único envío con suma final
        time: totalTime,
        lastVisited: ROUTES.SCENARIO3_FIN,
        correct: correctFinal
      });

      if (res.status === 201) {
        setIsCompleted(true);
        navigate(ROUTES.SCENARIO3_FIN);
      }
    } catch (error) {
      const status = error?.response?.status;
      const code = error?.response?.data?.code;

      if (status === 409 && code === "E_DUPLICATE_ANSWER") {
        await modalQuestionResolution({
          correct: false,
          question: "Respuesta ya registrada",
          textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, pueden continuar con el resto del juego.</div>`,
          customConfirmButtonHtml: "Ir a la conclusión",
        });
        navigate(ROUTES.SCENARIO3_FIN);
        return;
      }

      await modalQuestionResolution({
        correct: false,
        question: "No se pudo guardar",
        textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
        customConfirmButtonHtml: "Aceptar",
      });

      setIsSubmitting(false);
    }
  }

  const useHint = async () => {
    const hint = 'Fíjate en las edades de los pacientes que sí cruzaron y en las de los que no cruzaron. ¿Qué edad tienen en común los que no pudieron pasar? El umbral debe ser mayor que esa edad, pero igual o menor que la edad del más joven que sí logró cruzar.';
    if (hintUsed) {
      await modalHintShow({ hint, imageHintUsed, color: "violet" });
      return;
    }
    const { used } = await modalHint({ image: imageHint, imageHintUsed, hint, color: "violet" });
    if (used && !hintUsed) {
      setHintUsed(true);
      setHintPenalty(3); // se aplicará 1 sola vez en el primer intento que compute puntos (fallo o acierto)
    }
  };

  function checkAnswer() {
    const trimmed = (inputValue ?? "").toString().trim();
    if (trimmed === "") {
      modalQuestionResolution({
        correct: false,
        textHtml: `<img src=${estasseguro} alt="Debes ingresar un valor" class="estasseguro" />
                   <div class="text-center txtTitulos mt-3 violet">Debes ingresar un valor</div>`,
      });
      return;
    }

    const userValue = parseInt(trimmed, 10);
    if (Number.isNaN(userValue)) {
      modalQuestionResolution({
        correct: false,
        textHtml: `<img src=${estasseguro} alt="Valor inválido" class="estasseguro" />
                   <div class="text-center txtTitulos mt-3 violet">Introduce un número válido</div>`,
      });
      return;
    }

    const isCorrect = userValue === CORRECT_ANSWER;

    modalConfirm({ estasseguro, color: "violet" }).then((res) => {
      if (!res.isConfirmed) return;

      setIsSubmitting(true);
      const hintThisAttempt = (hintUsed && !hintAppliedToScore) ? hintPenalty : 0;

      if (isCorrect) {
        // ✅ Correcto (1º o 2º intento)
        const base = (attempt === 1) ? CORRECT_ANSWER_POINTS : CORRECT_ANSWER_POINTS_2;
        const attemptDelta = base - hintThisAttempt;
        const newScore = pendingScore + attemptDelta;
        if (hintThisAttempt) setHintAppliedToScore(true);

        modalQuestionResolution({
          correct: true,
          question: "Mayor a _____ años",
          textHtml: `<img src=${respuestacorrecta} alt="Respuesta correcta" style='max-width:420px' />
                     <div class="txt32 bold pt-4">Ganaron ${attemptDelta} puntos en este intento${hintThisAttempt ? " (-3 pista)" : ""}</div>
                     ${attempt === 2 ? `<div class="txt20 mt-2">Correcto en el segundo intento</div>` : ""}
                     <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>`,
          customConfirmButtonHtml: "Continuar",
          color: "violet"
        }).then(() => {
          submitAnswer(newScore, true); // envío único final
        });

        return;
      }

      // ❌ Incorrecto
      if (attempt < NUMBERATTEMPTS) {
        // 1er fallo: resta -5 (y -3 si aplica pista), NO enviamos aún
        const attemptDelta = WRONG_ANSWER_POINTS - hintThisAttempt; // -5 o -8 si pista aquí
        const newScore = pendingScore + attemptDelta;
        if (hintThisAttempt) setHintAppliedToScore(true);

        modalQuestionResolution({
          correct: false,
          question: "Mayor a _____ años",
          textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:420px' />
                     <div class="txt28 bold pt-3">Respuesta incorrecta ${attemptDelta} puntos${hintThisAttempt ? " (incluye -3 por pista)" : ""}</div>
                     <div class="txt22 mt-2">Les queda <b>1</b> intento. Ajusta el valor e inténtalo de nuevo.</div>
                     <div class="txt20 mt-2">Acumulado hasta ahora: <b>${newScore}</b></div>`,
          customConfirmButtonHtml: "Intentar de nuevo",
          color: "violet"
        }).then(() => {
          setPendingScore(newScore);
          setAttempt(attempt + 1);
          // Opcional: mantener el input para que lo corrijan; si prefieres limpiar, descomenta:
          // setInputValue("");
          setIsSubmitting(false);
        });

        return;
      }

      // 2º fallo: delta 0 (o -3 si justo aquí se aplica pista por 1ª vez), ahora sí enviamos
      const attemptDelta = WRONG_ANSWER_POINTS_2 - hintThisAttempt; // 0 o -3
      const newScore = pendingScore + attemptDelta;
      if (hintThisAttempt) setHintAppliedToScore(true);

      modalQuestionResolution({
        correct: false,
        question: "Mayor a _____ años",
        textHtml: `<img src=${respuestaincorrecta} alt="Respuesta incorrecta" style='max-width:420px' />
                   <div class="txt32 bold pt-4">Han agotado los intentos</div>
                   ${hintThisAttempt ? `<div class="txt20 mt-2">Se aplicó -3 por pista en este intento</div>` : ""}
                   <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>`,
        customConfirmButtonHtml: "Continuar",
        color: "violet"
      }).then(() => {
        submitAnswer(newScore, false); // envío único final
      });
    });
  }

  const remainingAttempts = NUMBERATTEMPTS - attempt + 1;

  return (
    <div className="h-100">
      <div className="container-fluid position-relative h-100 bgCircleViolet2">
        <div className="row h-100 d-flex align-items-center">
          <div className="col-md-12 h-100 d-flex flex-column justify-content-center px-4">
            <p className="txtTexto2 recuadro recuadro-violet py-4" style={{backgroundColor: "#FFFFFFAA"}}>
              En el hospital donde te encuentras los pacientes con cáncer de próstata<br/>deben atravesar la Puerta del Tiempo para recibir una valoración geriátrica.<br/>La puerta solo se abre si cumples la edad mínima requerida.
            </p>

            <h3
              className="txtTexto2 violet bold position-relative"
              style={{ backgroundColor: "#FFFFFFAA", borderRadius: "10px", paddingTop: "10px", paddingBottom: "10px" }}
            >
              Usando las pistas, descubre cuál es la edad mínima para atravesar la Puerta del Tiempo.
            </h3>
            <div className="d-flex justify-content-between">
              <h5 className="mb-4 txt32 text-left my-2 violet">
                <IconWriting size={40} color="#8c3888" stroke={1} /> Escribe el valor en números
              </h5>
              <div className="mb-3">
                <img src={remainingAttempts == 2 ? dosIntentos : unIntento} className="imgIntentos" />
              </div>
            </div>

            <div className="d-flex align-items-center justify-content-start txtTexto2">
              <span className="me-2">Mayor de</span>
              <input
                type="number"
                className="form-control text-center mx-2 recuadro-violet"
                style={{ width: "80px", fontSize: "30px", color: "#000 !important" }}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                min={1}
                max={120}
              />
              <span className="ms-2">años</span>
            </div>

            <div className="d-flex justify-content-between mt-5">
              <div className="position-relative d-inline-block">
                <img
                  src={btnPistaViolet}
                  className={`btnPista mt-2 ${hintUsed ? "used" : ""}`}
                  onClick={useHint}
                  alt={hintUsed ? "Pista usada" : "Usar pista"}
                  title={hintUsed ? "Pista usada (-3)" : "Usar pista (-3 puntos)"}
                />
                {hintUsed && <span className="badge-penalty">-3</span>}
              </div>
              <CheckButton
                text="Comprobar respuesta"
                onClick={checkAnswer}
                disabled={isCompleted || isSubmitting}
                color="violet"
                loading={isSubmitting}
              />
            </div>
          </div>
        </div>
      </div>

      <ScenarioBar activeScenario={activeScenario} />
    </div>
  );
}

export default injectIntl(Scenario3EvaluacionGeriatricos);
